import React, { useState, useEffect } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api.ts";
import {
  formatPrice,
  getExpiryInfo,
  downloadGatePassAsPDF,
} from "../../utils/functions.js";
import {
  FileText,
  Download,
  Eye,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  X,
  Printer,
  Loader,
} from "lucide-react";
import {
  downloadGatePassPdf,
  streamGatePassPdf,
} from "../../services/gatePassService.ts";
import placeholder from "../../assets/placeholder.svg";
import GatePassFromImage from "./GatePassPopup";
import Pagination from "../common/Pagination.jsx";

const BuyGatePasses = () => {
  const { translate, language } = useLanguage();
  const { user } = useAuth();
  const [gatePasses, setGatePasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all"); // all, issued, pending, expired
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showGatePassModal, setShowGatePassModal] = useState(false);
  const [selectedGatePass, setSelectedGatePass] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [streamingId, setStreamingId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    type: "buyer",
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterStatus]);

  // Fetch gate passes from API
  useEffect(() => {
    const fetchGatePasses = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!user?.id) {
          setError("User not authenticated");
          setLoading(false);
          return;
        }
        const params = {
          page: currentPage,
          limit: pageSize,
          sortBy: "id",
          sortOrder: "desc",
          filters: {},
        };

        if (filterStatus !== "all") {
          params.filters.validityStatus = filterStatus;
        }

        // Add search parameter if provided
        if (debouncedSearch && debouncedSearch.trim() !== "") {
          params.search = debouncedSearch.trim();
        }

        let response = null;

        if (user?.buyer?.id) {
          response = await api.get(`/gatepasses/buyer/${user?.buyer?.id}`, {
            params,
          });
        } else {
          response = await api.get(`/gatepasses/seller/${user?.seller?.id}`, {
            params,
          });
        }

        if (response.data?.pagination) {
          setPagination({
            currentPage: response.data?.pagination?.page,
            totalPages: response.data?.pagination?.totalPages,
            totalItems: response.data?.pagination?.total,
            itemsPerPage: response.data?.pagination?.limit,
          });
        }

        const gatePassesData = response.data.data || response.data || [];

        if (!Array.isArray(gatePassesData)) {
          console.error("API response data is not an array:", gatePassesData);
          setGatePasses([]);
          return;
        }

        setGatePasses(gatePassesData);
      } catch (error) {
        console.error("Error fetching gate passes:", error);
        setError("Failed to load gate passes. Please try again.");
        setGatePasses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGatePasses();
  }, [user?.id, currentPage, pageSize, debouncedSearch, filterStatus]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getGatePassStatus = (gatePass, translateFn) => {
    const currentDate = new Date();
    const expiryDate = new Date(gatePass.validTillDate);
    const daysUntilExpiry = Math.ceil(
      (expiryDate - currentDate) / (1000 * 60 * 60 * 24)
    );
    if (daysUntilExpiry < 0) {
      return {
        text: translateFn("historyGatePass.status.expired"),
        color: "text-white bg-red-700",
        icon: <XCircle className='w-4 h-4' />,
      };
    }
    return {
      text: translateFn("historyGatePass.status.valid"),
      color: "text-white bg-green-600",
      icon: <CheckCircle className='w-4 h-4' />,
    };
  };

  const handleDownloadGatePass = async (gatePass) => {
    try {
      setDownloadingId(gatePass?.id);
      const blob = await downloadGatePassPdf(gatePass?.id);
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `gatepass-${gatePass?.gatePassNumber || gatePass?.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 10000);
    } catch (error) {
      console.error("Error downloading gate pass PDF:", error);
      alert("Failed to download gate pass PDF. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleViewGatePass = async (gatePass) => {
    try {
      setStreamingId(gatePass?.id);
      const blob = await streamGatePassPdf(gatePass?.id);
      const href = URL.createObjectURL(blob);
      window.open(href, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(href), 30000);
    } catch (error) {
      console.error("Error streaming gate pass PDF:", error);
      alert("Failed to open gate pass PDF. Please try again.");
    } finally {
      setStreamingId(null);
    }
  };

  const handleCloseModal = () => {
    setShowGatePassModal(false);
    setSelectedGatePass(null);
  };

  const mapGatePassData = (gatePass) => {
    if (!gatePass) return {};

    return {
      gateNo: gatePass.gatePassNumber || gatePass.id || "-",
      issuedDate: formatDate(gatePass.issueDate || gatePass.createdAt) || "-",
      custName: user?.name || gatePass?.customerName || "-",
      custEmail: user?.email || gatePass?.customerEmail || "",
      custPhone: user?.mobileNo || gatePass?.customerPhone || "-",
      handOverTo: "Customer",
      recoveryName: "-",
      recoveryPhone: "-",
      invNo: gatePass?.invoiceNumber || gatePass?.id || "-",
      vinNo: gatePass?.vehicle?.vinNumber || gatePass?.vinNumber || "-",
      make: gatePass?.vehicle?.make || gatePass?.make || "-",
      model: gatePass?.vehicle?.model || gatePass?.model || "-",
      year: gatePass?.vehicle?.year || gatePass?.year || "-",
      color: gatePass?.vehicle?.color || gatePass?.color || "-",
      auctionDate: formatDate(gatePass?.auctionDate) || "-",
      hammer: gatePass?.hammerPrice || gatePass?.price || 0,
      total: gatePass?.totalAmount || gatePass?.price || 0,
      paymentRef: gatePass?.paymentReference || "-",
      verifyUrl: `https://khataljazeera.com/verify?no=${
        gatePass.gatePassNumber || gatePass.id
      }`,
    };
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center h-48 sm:h-64'>
        <div className='animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-[#f64f2d]'></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='text-center py-8 sm:py-12 px-4'>
        <div className='text-red-600 mb-3 sm:mb-4 text-sm sm:text-base'>
          {error}
        </div>
        <button
          onClick={() => window.location.reload()}
          className='px-3 sm:px-4 py-1.5 sm:py-2 bg-[#f64f2d] text-white rounded-[6px] sm:rounded-lg hover:bg-orange-600 text-xs sm:text-sm font-medium transition-colors'
        >
          Retry
        </button>
      </div>
    );
  }

  const isRTL = language === "ar";

  return (
    <div
      className='bg-[#FFF9F9] rounded-lg sm:rounded-xl shadow-md'
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Header*/}
      <div className='bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-lg sm:rounded-t-[16px] px-1 py-1'>
        <div className='p-4 sm:p-5 md:p-[20px] text-base sm:text-lg rounded-t-[8px] sm:rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2'>
          <div className='flex items-center justify-between'>
            <h2 className='text-base sm:text-lg md:text-xl font-semibold'>
              {translate("historyGatePass.heading")}
            </h2>
            <span className='text-xs sm:text-sm font-bold text-black'>
              {translate("historyGatePass.totalGatePasses")}:{" "}
              {pagination?.totalItems || 0}
            </span>
          </div>
        </div>
      </div>

      <div className='space-y-2 sm:space-y-3 px-3 sm:px-4 md:px-5 py-3 sm:py-4'>
        {/* Filter Navigation */}
        <div className='border-b border-gray-200'>
          <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 py-3 sm:py-4'>
            <nav
              className={`flex flex-nowrap gap-1 sm:gap-1.5 md:gap-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              {[
                { key: "all", label: translate("historyGatePass.filters.all") },
                {
                  key: "valid",
                  label: translate("historyGatePass.filters.valid"),
                },
                {
                  key: "expired",
                  label: translate("historyGatePass.filters.expired"),
                },
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setFilterStatus(filter.key)}
                  className={`px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-full font-medium text-[10px] sm:text-[11px] md:text-xs transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                    filterStatus === filter.key
                      ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                  }`}
                >
                  <span>{filter.label}</span>
                  {/* <span className="ml-1">({pagination?.totalItems || 0})</span> */}
                </button>
              ))}
            </nav>

            {/* Search Field */}
            <div className='flex items-center w-full lg:w-auto gap-2.5 sm:gap-3'>
              <div className='relative flex-1 lg:flex-none'>
                <input
                  type='text'
                  placeholder={translate("historyGatePass.searchPlaceholder")}
                  className={`w-full sm:w-64 md:w-80 px-4 sm:px-5 py-2 sm:py-2.5 md:py-3 ${
                    isRTL ? "pr-10 sm:pr-12" : "pl-10 sm:pl-12"
                  } border border-gray-300 rounded-[8px] sm:rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-xs sm:text-sm`}
                  value={searchTerm || ""}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <svg
                  className={`absolute ${
                    isRTL ? "right-3 sm:right-4" : "left-3 sm:left-4"
                  } top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-orange-500`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className='p-2 sm:p-2.5 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0'
                  title='Clear search'
                >
                  <svg
                    className='w-4 h-4 sm:w-5 sm:h-5'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Gate Passes List */}
        <div className='space-y-2 sm:space-y-3'>
          {gatePasses.length === 0 ? (
            <div className='text-center py-8 sm:py-12 px-4'>
              <FileText className='w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-3 sm:mb-4' />
              <h3 className='text-base sm:text-lg font-medium text-gray-900 mb-1.5 sm:mb-2'>
                {translate("historyGatePass.messages.noGatePassesFound")}
              </h3>
              <p className='text-sm sm:text-base text-gray-500'>
                {filterStatus === "all"
                  ? translate("historyGatePass.messages.noGatePassesYet")
                  : translate(
                      "historyGatePass.messages.noFilteredGatePasses"
                    ).replace(
                      "{status}",
                      translate(`historyGatePass.filters.${filterStatus}`)
                    )}
              </p>
            </div>
          ) : (
            <>
              {gatePasses.map((gatePass) => {
                const status = getGatePassStatus(gatePass, translate);
                return (
                  <div
                    key={gatePass.id}
                    className='bg-white border border-gray-200 rounded-lg sm:rounded-xl p-4 sm:p-5 md:p-6 hover:shadow-md transition-shadow'
                  >
                    {/* Mobile Layout */}
                    <div className='block lg:hidden'>
                      {/* Header with Status */}
                      <div className='flex items-center justify-between mb-3 sm:mb-4'>
                        <div className='flex items-center space-x-2 sm:space-x-2.5 flex-1 min-w-0'>
                          <FileText className='w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0' />
                          <h3 className='text-sm sm:text-base font-semibold text-black truncate'>
                            {translate("historyGatePass.labels.gatePassNumber")}{" "}
                            {gatePass.gatePassNumber || gatePass.id}
                          </h3>
                        </div>
                        <span
                          className={`px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium flex items-center ${
                            isRTL ? "space-x-reverse" : "space-x-1"
                          } flex-shrink-0 ${status.color}`}
                        >
                          <span className='w-3.5 h-3.5 sm:w-4 sm:h-4 flex items-center justify-center'>
                            {status.icon}
                          </span>
                          <span>{status.text}</span>
                        </span>
                      </div>

                      {/* Vehicle Image and Basic Info */}
                      <div className='flex items-start space-x-3 sm:space-x-4 mb-3 sm:mb-4'>
                        <div className='w-20 h-20 sm:w-24 sm:h-24 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0'>
                          {gatePass?.vehicle?.images?.[0]?.imageUrl && (
                            <img
                              src={
                                gatePass.vehicle.images?.[0].imageUrl ||
                                placeholder
                              }
                              alt='Vehicle'
                              className='w-full h-full object-cover'
                            />
                          )}
                        </div>
                        <div className='flex-1 min-w-0'>
                          <p className='text-sm sm:text-base font-semibold text-black leading-tight break-words'>
                            {gatePass?.vehicle?.name ||
                              gatePass?.vehicleName ||
                              "-"}
                          </p>
                          <p className='text-xs sm:text-sm text-gray-500 mt-1'>
                            Lot #
                            {gatePass?.vehicle?.lotNumber ||
                              gatePass?.lotNumber ||
                              "-"}
                          </p>
                          <p className='text-sm sm:text-base font-semibold text-[#f64f2d] mt-1.5'>
                            {formatPrice(
                              gatePass?.vehicle?.soldPrice ||
                                gatePass?.soldPrice ||
                                0
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Dates Row */}
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4'>
                        <div className='bg-gray-50 rounded-lg p-2.5 sm:p-3'>
                          <p className='text-xs sm:text-sm text-gray-500 mb-1.5'>
                            {translate("historyGatePass.labels.issueDate")}
                          </p>
                          <p className='text-xs sm:text-sm font-semibold text-gray-900 leading-relaxed'>
                            {formatDate(
                              gatePass?.issueDate || gatePass?.createdAt
                            )}
                          </p>
                        </div>
                        <div className='bg-gray-50 rounded-lg p-2.5 sm:p-3'>
                          <p className='text-xs sm:text-sm text-gray-500 mb-1.5'>
                            {translate("historyGatePass.labels.expiryDate")}
                          </p>
                          <p className='text-xs sm:text-sm font-semibold leading-relaxed'>
                            {(() => {
                              // Import the new function from functions.js
                              // import { getExpiryInfo } from "../../utils/functions.js";
                              // But since imports are at the top, assume getExpiryInfo is available

                              // Use issueDate or createdAt as the base date
                              const validTillDays = gatePass?.validTillDate;
                              const {
                                expiryDateStr,
                                dayOfWeek,
                                daysRemaining,
                                color,
                              } = getExpiryInfo(validTillDays);

                              return (
                                <span style={{ color }}>
                                  {expiryDateStr} ({dayOfWeek})
                                  {typeof daysRemaining === "number" && (
                                    <span
                                      className={`${
                                        isRTL ? "mr-1" : "ml-1"
                                      } text-xs`}
                                    >
                                      <br />
                                      {daysRemaining}{" "}
                                      {translate(
                                        "historyGatePass.labels.daysLeft"
                                      )}
                                    </span>
                                  )}
                                </span>
                              );
                            })()}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className='flex flex-col sm:flex-row gap-2 sm:gap-3 mb-3 sm:mb-4'>
                        <button
                          onClick={() => handleViewGatePass(gatePass)}
                          disabled={streamingId === gatePass?.id}
                          className='flex-1 sm:flex-none flex items-center justify-center px-4 sm:px-5 py-2.5 sm:py-3 bg-[#f64f2d] text-white rounded-lg hover:bg-orange-600 transition-colors text-sm sm:text-base font-medium disabled:opacity-70 shadow-sm'
                        >
                          {streamingId === gatePass?.id ? (
                            <Loader className='w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin' />
                          ) : (
                            <Eye className='w-4 h-4 sm:w-5 sm:h-5 mr-2' />
                          )}
                          <span>
                            {translate("historyGatePass.buttons.view")}
                          </span>
                        </button>
                        <button
                          onClick={() => handleDownloadGatePass(gatePass)}
                          disabled={downloadingId === gatePass?.id}
                          className={`flex-1 sm:flex-none flex items-center justify-center px-4 sm:px-5 py-2.5 sm:py-3 bg-orange-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base font-medium disabled:opacity-70 shadow-sm ${
                            isRTL ? "flex-row-reverse" : ""
                          }`}
                        >
                          {downloadingId === gatePass?.id ? (
                            <Loader
                              className={`w-4 h-4 sm:w-5 sm:h-5 ${
                                isRTL ? "ml-2" : "mr-2"
                              } animate-spin`}
                            />
                          ) : (
                            <Download
                              className={`w-4 h-4 sm:w-5 sm:h-5 ${
                                isRTL ? "ml-2" : "mr-2"
                              }`}
                            />
                          )}
                          <span>
                            {translate("historyGatePass.buttons.download")}
                          </span>
                        </button>
                        {/* <button
                  onClick={() => handleDownloadGatePass(gatePass)}
                  className="flex-1 flex items-center justify-center px-3 py-2 border border-[#f64f2d] text-[#f64f2d] rounded-md hover:bg-orange-50 transition-colors text-sm"
                >
                  <Download className="w-4 h-4 mr-1" />
                  <span>Download</span>
                </button> */}
                      </div>

                      {/* Additional Information - Collapsible on Mobile */}
                      <details className='bg-gray-50 rounded-lg p-3 sm:p-4'>
                        <summary className='font-medium text-gray-900 text-sm sm:text-base cursor-pointer list-none flex items-center justify-between'>
                          <span>
                            {translate(
                              "historyGatePass.labels.gatePassDetails"
                            )}
                          </span>
                          <span className='text-gray-400 text-xs'>▼</span>
                        </summary>
                        <div className='mt-3 space-y-2.5 sm:space-y-3 text-xs sm:text-sm pt-3 border-t border-gray-200'>
                          <div className='flex flex-col sm:flex-row sm:items-center'>
                            <span
                              className={`text-gray-500 font-medium mb-1 sm:mb-0 sm:w-24 ${
                                isRTL ? "sm:ml-2" : "sm:mr-2"
                              }`}
                            >
                              {translate("historyGatePass.labels.vinNumber")}:
                            </span>
                            <span className='font-medium text-gray-900 break-all'>
                              {gatePass?.vehicle?.vinNumber
                                ? gatePass.vehicle.vinNumber.length > 5
                                  ? "************" +
                                    gatePass.vehicle.vinNumber.slice(-5)
                                  : gatePass.vehicle.vinNumber
                                : "-"}
                            </span>
                          </div>
                          <div className='flex flex-col sm:flex-row sm:items-center'>
                            <span
                              className={`text-gray-500 font-medium mb-1 sm:mb-0 sm:w-24 ${
                                isRTL ? "sm:ml-2" : "sm:mr-2"
                              }`}
                            >
                              {translate("historyGatePass.labels.auction")}:
                            </span>
                            <span className='font-medium text-gray-900 break-words'>
                              {gatePass?.buyerInvoice?.buyerInvoiceDetails?.[0]
                                ?.auctionLane?.auction?.name || "N/A"}
                            </span>
                          </div>
                          <div className='flex flex-col sm:flex-row sm:items-center'>
                            <span
                              className={`text-gray-500 font-medium mb-1 sm:mb-0 sm:w-24 ${
                                isRTL ? "sm:ml-2" : "sm:mr-2"
                              }`}
                            >
                              {translate("historyGatePass.labels.location")}:
                            </span>
                            <span className='font-medium text-gray-900 break-words'>
                              {gatePass?.location ||
                                gatePass?.pickupLocation ||
                                "Block E - Al Sajaa - Emirates Industrial City - Sharjah"}
                            </span>
                          </div>
                          <div className='flex flex-col sm:flex-row sm:items-start'>
                            <span
                              className={`text-gray-500 font-medium mb-1 sm:mb-0 sm:w-24 ${
                                isRTL ? "sm:ml-2" : "sm:mr-2"
                              }`}
                            >
                              {translate("historyGatePass.labels.instructions")}
                              :
                            </span>
                            <span className='font-medium text-gray-900 break-words flex-1'>
                              {gatePass?.instructions ||
                                translate(
                                  "historyGatePass.messages.defaultInstructions"
                                )}
                            </span>
                          </div>
                        </div>
                      </details>
                    </div>

                    {/* Desktop Layout */}
                    <div className='hidden lg:block'>
                      <div className='flex items-start justify-between'>
                        {/* Gate Pass Details */}
                        <div className='flex-1'>
                          <div className='flex items-center space-x-2 md:space-x-3 mb-2 md:mb-3'>
                            <div className='flex items-center space-x-1.5 md:space-x-2'>
                              <FileText className='w-3.5 h-3.5 md:w-4 md:h-4 text-gray-400' />
                              <h3 className='text-sm md:text-base font-semibold text-black'>
                                {translate(
                                  "historyGatePass.labels.gatePassNumber"
                                )}{" "}
                                {gatePass.gatePassNumber || gatePass.id}
                              </h3>
                            </div>
                            <span
                              className={`px-2 md:px-2.5 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium flex items-center ${
                                isRTL ? "space-x-reverse" : "space-x-1"
                              } ${status.color}`}
                            >
                              <span className='w-3.5 h-3.5 md:w-4 md:h-4 flex items-center justify-center'>
                                {status.icon}
                              </span>
                              <span>{status.text}</span>
                            </span>
                          </div>

                          <div className='flex items-center justify-between mb-2 md:mb-3'>
                            <div className='flex items-center space-x-8 md:space-x-12 lg:space-x-16'>
                              {/* Vehicle Image */}
                              <div className='w-10 h-10 md:w-12 md:h-12 bg-gray-200 rounded-[6px] md:rounded-md overflow-hidden flex-shrink-0'>
                                {gatePass?.vehicle?.images?.[0]?.imageUrl && (
                                  <img
                                    src={
                                      gatePass.vehicle.images?.[0].imageUrl ||
                                      placeholder
                                    }
                                    alt='Vehicle'
                                    className='w-full h-full object-cover'
                                  />
                                )}
                              </div>

                              {/* Vehicle Name */}
                              <div>
                                <p className='text-[11px] md:text-xs font-semibold text-black'>
                                  {gatePass?.vehicle?.name ||
                                    gatePass?.vehicleName ||
                                    "N/A"}
                                </p>
                                <p className='text-[10px] md:text-xs text-gray-500'>
                                  Lot #
                                  {gatePass?.vehicle?.lotNumber ||
                                    gatePass?.lotNumber ||
                                    "N/A"}
                                </p>
                              </div>

                              {/* Issue Date */}
                              <div className='bg-gray-50 rounded-md px-3 py-2'>
                                <p className='text-[10px] md:text-xs text-gray-500 mb-0.5'>
                                  {translate(
                                    "historyGatePass.labels.issueDate"
                                  )}
                                </p>
                                <p className='text-[11px] md:text-xs font-semibold text-gray-900'>
                                  {formatDate(
                                    gatePass?.issueDate || gatePass?.createdAt
                                  )}
                                </p>
                              </div>

                              {/* Expiry Date */}
                              <div className='bg-gray-50 rounded-md px-3 py-2'>
                                <p className='text-[10px] md:text-xs text-gray-500 mb-0.5'>
                                  {translate(
                                    "historyGatePass.labels.expiryDate"
                                  )}
                                </p>
                                <p className='text-[11px] md:text-xs font-semibold text-gray-900'>
                                  {(() => {
                                    // Import the new function from functions.js
                                    // import { getExpiryInfo } from "../../utils/functions.js";
                                    // But since imports are at the top, assume getExpiryInfo is available

                                    // Use issueDate or createdAt as the base date
                                    const validTillDays =
                                      gatePass?.validTillDate;
                                    const {
                                      expiryDateStr,
                                      dayOfWeek,
                                      daysRemaining,
                                      color,
                                    } = getExpiryInfo(validTillDays);

                                    return (
                                      <span style={{ color }}>
                                        {expiryDateStr} ({dayOfWeek})
                                        {typeof daysRemaining === "number" && (
                                          <span
                                            className={`${
                                              isRTL ? "mr-1" : "ml-1"
                                            } text-[10px] md:text-xs`}
                                          >
                                            {daysRemaining}{" "}
                                            {translate(
                                              "historyGatePass.labels.daysLeft"
                                            )}
                                          </span>
                                        )}
                                      </span>
                                    );
                                  })()}
                                </p>
                              </div>

                              {/* Vehicle Price */}
                              {/* <div>
                              <p className="text-xs text-gray-500">
                                Vehicle Price
                              </p>
                              <p className="text-xs font-semibold text-[#f64f2d]">
                                {formatPrice(
                                  gatePass?.vehicle?.soldPrice ||
                                    gatePass?.soldPrice ||
                                    0
                                )}
                              </p>
                            </div> */}
                            </div>
                          </div>

                          {/* Additional Information */}
                          <div className='bg-gray-50 rounded-[6px] md:rounded-md p-2 md:p-3'>
                            <h4 className='font-medium text-gray-900 mb-1 text-xs md:text-sm'>
                              {translate(
                                "historyGatePass.labels.gatePassDetails"
                              )}
                            </h4>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-1 text-[10px] md:text-xs'>
                              <div>
                                <span className='text-gray-500'>
                                  {translate(
                                    "historyGatePass.labels.vinNumber"
                                  )}
                                  :
                                </span>
                                <span
                                  className={`${
                                    isRTL ? "mr-1.5 md:mr-2" : "ml-1.5 md:ml-2"
                                  } font-medium`}
                                >
                                  {gatePass?.vehicle?.vinNumber
                                    ? gatePass.vehicle.vinNumber.length > 5
                                      ? "************" +
                                        gatePass.vehicle.vinNumber.slice(-5)
                                      : gatePass.vehicle.vinNumber
                                    : "-"}
                                </span>
                              </div>
                              <div>
                                <span className='text-gray-500'>
                                  {translate("historyGatePass.labels.auction")}:
                                </span>
                                <span
                                  className={`${
                                    isRTL ? "mr-1.5 md:mr-2" : "ml-1.5 md:ml-2"
                                  } font-medium`}
                                >
                                  {gatePass?.buyerInvoice
                                    ?.buyerInvoiceDetails?.[0]?.auctionLane
                                    ?.auction?.name || "N/A"}
                                </span>
                              </div>
                              <div>
                                <span className='text-gray-500'>
                                  {translate("historyGatePass.labels.location")}
                                  :
                                </span>
                                <span
                                  className={`${
                                    isRTL ? "mr-1.5 md:mr-2" : "ml-1.5 md:ml-2"
                                  } font-medium`}
                                >
                                  {gatePass?.location ||
                                    gatePass?.pickupLocation ||
                                    "Block E - Al Sajaa - Emirates Industrial City - Sharjah"}
                                </span>
                              </div>
                              <div>
                                <span className='text-gray-500'>
                                  {translate(
                                    "historyGatePass.labels.instructions"
                                  )}
                                  :
                                </span>
                                <span
                                  className={`${
                                    isRTL ? "mr-1.5 md:mr-2" : "ml-1.5 md:ml-2"
                                  } font-medium`}
                                >
                                  {gatePass?.instructions ||
                                    translate(
                                      "historyGatePass.messages.defaultInstructions"
                                    )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className='flex flex-col space-y-1 ml-2 md:ml-4'>
                          <button
                            onClick={() => handleViewGatePass(gatePass)}
                            disabled={streamingId === gatePass?.id}
                            className={`flex-1 flex items-center justify-center px-2.5 md:px-3 py-1.5 md:py-2 bg-[#f64f2d] text-white rounded-[6px] md:rounded-md hover:bg-orange-600 transition-colors text-[11px] md:text-sm disabled:opacity-70 ${
                              isRTL ? "flex-row-reverse" : ""
                            }`}
                          >
                            {streamingId === gatePass?.id ? (
                              <Loader
                                className={`w-3.5 h-3.5 md:w-4 md:h-4 ${
                                  isRTL ? "ml-0.5 md:ml-1" : "mr-0.5 md:mr-1"
                                } animate-spin`}
                              />
                            ) : (
                              <Eye
                                className={`w-3.5 h-3.5 md:w-4 md:h-4 ${
                                  isRTL ? "ml-0.5 md:ml-1" : "mr-0.5 md:mr-1"
                                }`}
                              />
                            )}
                            <span>
                              {translate("historyGatePass.buttons.view")}
                            </span>
                          </button>
                          <button
                            onClick={() => handleDownloadGatePass(gatePass)}
                            disabled={downloadingId === gatePass?.id}
                            className={`flex-1 flex items-center justify-center px-2.5 md:px-3 py-1.5 md:py-2 border border-[#f64f2d] text-[#f64f2d] rounded-[6px] md:rounded-md hover:bg-[#f64f2d] hover:text-white transition-colors text-[11px] md:text-sm disabled:opacity-70 ${
                              isRTL ? "flex-row-reverse" : ""
                            }`}
                          >
                            {downloadingId === gatePass?.id ? (
                              <Loader
                                className={`w-3.5 h-3.5 md:w-4 md:h-4 ${
                                  isRTL ? "ml-0.5 md:ml-1" : "mr-0.5 md:mr-1"
                                } animate-spin`}
                              />
                            ) : (
                              <Download
                                className={`w-3.5 h-3.5 md:w-4 md:h-4 ${
                                  isRTL ? "ml-0.5 md:ml-1" : "mr-0.5 md:mr-1"
                                }`}
                              />
                            )}
                            <span>
                              {translate("historyGatePass.buttons.download")}
                            </span>
                          </button>
                          {/* <button
                    onClick={() => handleDownloadGatePass(gatePass)}
                    className="flex items-center justify-center px-3 py-1.5 border border-[#f64f2d] text-[#f64f2d] rounded-md hover:bg-orange-50 transition-colors text-sm"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    <span>Download</span>
                  </button> */}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {pagination?.totalPages > 1 && (
                <div className='px-3 sm:px-4 md:px-6 py-3 sm:py-4 flex justify-center sm:justify-end'>
                  <Pagination
                    totalPages={pagination.totalPages}
                    currentPage={pagination.currentPage}
                    setCurrentPage={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

        {/* Gate Pass Modal */}
        {showGatePassModal && selectedGatePass && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-3 md:p-4'>
            <div className='bg-white rounded-[8px] sm:rounded-lg shadow-xl max-w-[650px] w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto'>
              <div className='flex items-center justify-between p-3 sm:p-4 border-b'>
                <h3 className='text-base sm:text-lg md:text-xl font-semibold text-gray-900'>
                  {translate("historyGatePass.labels.vehicleOutPass")}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className='text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0'
                >
                  <X className='w-5 h-5 sm:w-6 sm:h-6' />
                </button>
              </div>
              <div className='p-2 sm:p-3 md:p-4'>
                <GatePassFromImage data={mapGatePassData(selectedGatePass)} />
              </div>
              <div className='flex justify-end p-3 sm:p-4 border-t'>
                <button
                  onClick={handleCloseModal}
                  className='px-4 sm:px-6 py-1.5 sm:py-2 bg-[#f64f2d] text-white rounded-[6px] sm:rounded-lg hover:bg-orange-600 transition-colors text-xs sm:text-sm font-medium'
                >
                  {translate("historyGatePass.buttons.close")}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default BuyGatePasses;
